import React, { useState, useEffect, useCallback } from "react";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import {
  fetchHygieneScoreTrends,
  fetchComplianceReports,
  fetchCombinedReport,
  exportReportsToExcel,
  HygieneScoreTrend,
  ComplianceReport,
  ReportFilters,
} from "./services/washroomReportsService";

// Define the frequency type
type Frequency = "daily" | "weekly" | "monthly" | "yearly";

const WashroomReports: React.FC = () => {
  // State for filters
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // Add new state for frequency, defaulting to 'weekly'
  const [frequency, setFrequency] = useState<Frequency>("weekly");

  // State for data
  const [hygieneTrends, setHygieneTrends] = useState<HygieneScoreTrend[]>([]);
  const [complianceData, setComplianceData] = useState<ComplianceReport[]>([]);
  const [summary, setSummary] = useState<any>({});

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [exportLoading, setExportLoading] = useState(false);

  // Process trends data for chart
  const processTrendsForChart = useCallback((trends: HygieneScoreTrend[]) => {
    if (!trends.length) return [];

    // Group by date and calculate average score per date
    const groupedByDate = trends.reduce(
      (acc, trend) => {
        const date = trend.date;
        if (!acc[date]) {
          acc[date] = {
            date,
            scores: [],
            count: 0,
          };
        }
        // Handle potential null/undefined scores
        const score = trend.hygieneScore || 0;
        acc[date].scores.push(score);
        acc[date].count += trend.auditCount || 1;
        return acc;
      },
      {} as Record<string, { date: string; scores: number[]; count: number }>,
    );

    // Calculate average score for each date and convert to chart format
    const processedTrends = Object.values(groupedByDate)
      .map((item) => {
        const avgScore =
          item.scores.length > 0
            ? item.scores.reduce((sum, score) => sum + score, 0) /
              item.scores.length
            : 0;

        const date = new Date(item.date);
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];

        return {
          month: monthNames[date.getMonth()],
          value: Math.round(avgScore * 100) / 100,
          date: item.date, // Keep original date for sorting
          originalDate: date,
        };
      })
      .sort((a, b) => a.originalDate.getTime() - b.originalDate.getTime());

    return processedTrends;
  }, []);

  const chartData = processTrendsForChart(hygieneTrends);

  // Update fetchData to include frequency in the filters
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const filters: ReportFilters = {};
      if (selectedRegion) filters.region = selectedRegion;
      if (selectedPremises) filters.premises = selectedPremises;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      if (frequency) {
        const frequencyMapping: Record<
          Frequency,
          "Daily" | "Weekly" | "Monthly" | "Yearly"
        > = {
          daily: "Daily",
          weekly: "Weekly",
          monthly: "Monthly",
          yearly: "Yearly",
        };
        filters.frequency = frequencyMapping[frequency]; // Map lowercase to capitalized
      }

      // Fetch combined report for better performance
      const combinedReport = await fetchCombinedReport(filters);

      setHygieneTrends(combinedReport.data.hygieneScoreTrends || []);
      setComplianceData(combinedReport.data.complianceReports || []);
      setSummary(combinedReport.summary || {});
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch report data",
      );
      console.error("Error fetching washroom reports:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedRegion, selectedPremises, startDate, endDate, frequency]); // Add frequency to dependency array

  // Update handleExportReports to include frequency
  const handleExportReports = useCallback(async () => {
    try {
      setExportLoading(true);
      setError("");

      const filters: ReportFilters = {};
      if (selectedRegion) filters.region = selectedRegion;
      if (selectedPremises) filters.premises = selectedPremises;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      if (frequency) {
        const frequencyMapping: Record<
          Frequency,
          "Daily" | "Weekly" | "Monthly" | "Yearly"
        > = {
          daily: "Daily",
          weekly: "Weekly",
          monthly: "Monthly",
          yearly: "Yearly",
        };
        filters.frequency = frequencyMapping[frequency]; // Map lowercase to capitalized
      }

      await exportReportsToExcel(filters);

      // Show success message (optional)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to export reports");
      console.error("Error exporting reports:", err);
    } finally {
      setExportLoading(false);
    }
  }, [selectedRegion, selectedPremises, startDate, endDate, frequency]); // Add frequency to dependency array

  // Fetch data when filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle region change
  const handleRegionChange = (value: string) => {
    setSelectedRegion(value);
    // Reset premises when region changes
    setSelectedPremises("");
  };

  // Handle premises change
  const handlePremisesChange = (value: string) => {
    setSelectedPremises(value);
  };

  // Handle frequency change
  const handleFrequencyChange = (selectedFrequency: Frequency) => {
    setFrequency(selectedFrequency);
  };

  const createSmoothPath = (points: { x: number; y: number }[]) => {
    if (points.length < 2) return "";
    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const next = points[i + 1];
      const cp1x = prev.x + (curr.x - prev.x) * 0.3;
      const cp1y = prev.y;
      const cp2x = curr.x - (next ? (next.x - curr.x) * 0.3 : 0);
      const cp2y = curr.y;
      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${curr.x} ${curr.y}`;
    }
    return path;
  };

  const chartWidth = 1200;
  const chartHeight = 400;
  const padding = 30;

  const dataPoints = chartData.map((d, i) => ({
    x:
      padding +
      (i * (chartWidth - 2 * padding)) / Math.max(chartData.length - 1, 1),
    y:
      chartHeight -
      padding -
      ((d.value - 50) / 50) * (chartHeight - 2 * padding),
  }));

  const smoothPath = createSmoothPath(dataPoints);

  // Use summary data for overall average score with proper fallbacks
  const overallAverageScore =
    summary?.averageHygieneScore !== undefined
      ? Math.round(summary.averageHygieneScore * 100) / 100
      : chartData.length > 0
        ? Math.round(
            (chartData.reduce((sum, item) => sum + (item.value || 0), 0) /
              chartData.length) *
              100,
          ) / 100
        : 0;

  return (
    <div className="px-2 py-4 mt-14  min-h-100vh">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#222] mt-2">
            Reports & Analytics
          </h1>
          <p className="text-[#7b7b7b] text-sm">
            Analyze washroom hygiene trends, compliance, and staff performance.
          </p>
        </div>
        <button
          onClick={handleExportReports}
          disabled={exportLoading}
          className={`mt-4 md:mt-0 ${
            exportLoading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-[#00A8A8] hover:bg-[#0b7979]"
          } text-white font-semibold px-6 py-2 rounded-full shadow-sm transition flex items-center gap-2`}
        >
          {exportLoading && (
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          )}
          {exportLoading ? "Exporting..." : "Export Reports"}
        </button>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4 text-[#333]">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* <div>
            <label className="block text-sm font-medium mb-2 text-[#555]">Region</label>
            <Dropdown
              type="region"
              selectedValue={selectedRegion}
              onChange={handleRegionChange}
            />
          </div> */}
          <div>
            <label className="block text-sm font-medium mb-2 text-[#555]">
              Premises
            </label>
            <Dropdown
              type="premises"
              selectedValue={selectedPremises}
              onChange={handlePremisesChange}
              selectedRegion={selectedRegion}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-[#555]">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-[#555]">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-6">
          Loading report data...
        </div>
      )}

      {/* Hygiene Score Trends Chart */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <h2 className="text-xl font-semibold mb-6 text-[#333]">
          Washroom Hygiene Score Trends
        </h2>

        <div className="flex items-center justify-start mb-4">
          <div className="mr-8">
            <div className="text-5xl font-bold text-[#333] mb-2">
              {overallAverageScore}%
            </div>
            <div className="text-green-600 font-semibold text-sm">
              Average hygiene score
            </div>
          </div>
        </div>

        {chartData.length > 0 ? (
          <div className="w-full overflow-hidden">
            <svg
              viewBox={`0 0 ${chartWidth} ${chartHeight}`}
              className="w-full h-[380px]"
            >
              <defs>
                <linearGradient
                  id="areaGradient"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#4285f4" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#4285f4" stopOpacity="0.05" />
                </linearGradient>
              </defs>

              {dataPoints.length > 1 && (
                <>
                  <path
                    d={`${smoothPath} L ${
                      dataPoints[dataPoints.length - 1].x
                    } ${chartHeight - padding} L ${dataPoints[0].x} ${
                      chartHeight - padding
                    } Z`}
                    fill="url(#areaGradient)"
                  />

                  <path
                    d={smoothPath}
                    fill="none"
                    stroke="#4285f4"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </>
              )}

              {dataPoints.map((point, i) => (
                <circle
                  key={i}
                  cx={point.x}
                  cy={point.y}
                  r="4"
                  fill="#4285f4"
                  stroke="white"
                  strokeWidth="2"
                />
              ))}

              {chartData.map((d, i) => (
                <text
                  key={`${d.month}-${i}`}
                  x={dataPoints[i]?.x || 0}
                  y={chartHeight - 10}
                  fontSize="14"
                  textAnchor="middle"
                  fill="#888"
                  fontWeight="500"
                >
                  {d.month}
                </text>
              ))}
            </svg>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No trend data available for the selected filters
          </div>
        )}
      </div>

      {/* Compliance Reports Table */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <h2 className="text-xl font-semibold text-[#333]">
            Compliance Reports
          </h2>

          {/* Updated frequency toggle buttons */}
          <div className="flex gap-2 mt-4 md:mt-0">
            {(["daily", "weekly", "monthly", "yearly"] as Frequency[]).map(
              (freq) => (
                <button
                  key={freq}
                  onClick={() => handleFrequencyChange(freq)}
                  className={`px-4 py-2 focus:outline-none rounded-full font-medium text-sm transition capitalize ${
                    frequency === freq
                      ? "bg-[#00A8A8] text-white shadow-sm"
                      : "bg-[#f1f3f4] text-[#5f6368] hover:bg-[#e8eaed]"
                  }`}
                >
                  {freq}
                </button>
              ),
            )}
          </div>
        </div>

        {complianceData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 font-medium text-[#5f6368] text-sm">
                    Washroom
                  </th>
                  <th className="text-left py-3 font-medium text-[#5f6368] text-sm">
                    Premises
                  </th>
                  <th className="text-left py-3 font-medium text-[#5f6368] text-sm">
                    Compliance Rate
                  </th>
                  <th className="text-left py-3 font-medium text-[#5f6368] text-sm">
                    Inspections
                  </th>
                  <th className="text-left py-3 font-medium text-[#5f6368] text-sm">
                    Average Score
                  </th>
                  <th className="text-left py-3 font-medium text-[#5f6368] text-sm">
                    Frequency
                  </th>
                </tr>
              </thead>
              <tbody>
                {complianceData.map((row, i) => (
                  <tr
                    key={`${row.washroomId}-${i}`}
                    className={`${
                      i !== complianceData.length - 1
                        ? "border-b border-gray-100"
                        : ""
                    }`}
                  >
                    <td className="py-4 text-[#9aa0a6] font-medium">
                      {row.washroomName || "N/A"}
                    </td>
                    <td className="py-4 text-[#9aa0a6] font-medium">
                      {row.premisesName || "N/A"}
                    </td>
                    <td className="py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-2 bg-[#f1f3f4] rounded-full overflow-hidden">
                          <div
                            className="h-2 bg-[#4285f4] rounded-full transition-all duration-300"
                            style={{
                              width: `${Math.min(
                                row.complianceRate || 0,
                                100,
                              )}%`,
                            }}
                          ></div>
                        </div>
                        <span className="font-semibold text-[#333] text-sm">
                          {row.complianceRate || 0}%
                        </span>
                      </div>
                    </td>
                    <td className="py-4 font-semibold text-[#333]">
                      {row.totalInspections || 0}
                    </td>
                    <td className="py-4 font-semibold text-[#333]">
                      {row.avgScore || row.completedAvgScore || 0}
                    </td>
                    <td className="py-4 text-[#9aa0a6] font-medium capitalize">
                      {(row as any).frequency || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No compliance data available for the selected filters
          </div>
        )}
      </div>
    </div>
  );
};

export default WashroomReports;
